import { useState, useEffect, useCallback } from "react";
import { loadFromSupabase, saveToSupabase } from "../supabaseDB";
import { getISOWeek, getPrevCurrentNextWeeks, buildWeightedFixedPrices, buildWeightedConsignmentPrices, fillWeekGaps } from "./weeklyWeightedPrices";

/* GFI Nexus — AI Analyst Log, Fase C of the Forecast v2 roadmap
   ─────────────────────────────────────────────────────────────────
   Real implementation (2026-09-15), replacing the Fase A ComingSoon
   placeholder. As of 2026-09-17, this shows only the ACTIVE next-week
   projection per category (never a scrolling history) — one
   ai_analyst_log row per (week being projected, category), refreshed
   in place by netlify/functions/ai-forecast-analysis.js as fresher
   firm orders/context arrive, and only for a category that actually
   has a real firm order this week to project from (see that file's
   header for the full weekly-cadence design). Nothing here ever
   auto-applies to a reported price — every entry starts 'pending' and
   needs an explicit Approve/Reject from Arturo, which is never reset
   back to pending by a later automatic refresh. */

var T = {
  en: {
    title: "AI Analyst Log", subtitle: "Claude-reasoned next-week price projection, refreshed as new firm orders/context arrive — review and approve/reject before it means anything operationally.",
    running: "Running…",
    runCurrentWeek: "Run current week", runNextWeek: "Run next week",
    runNoActiveCategories: "Ran successfully, but there wasn't enough historical data to build a statistical projection for either category — nothing to show yet.",
    empty: "No active projection yet — click Run now, or wait for tomorrow's automatic refresh.",
    baseForecast: "This week's price (model estimate)", adjustedPrice: "Next week's projection", adjustment: "Adjustment",
    thisWeekFirmOrderLabel: "Real order this week", thisWeekFirmOrderHint: "for comparison, not the model's base",
    projectionForWeek: "projection for week", lastRefreshed: "Last refreshed",
    accTrackRecord: "Recent track record", accAvgBias: "avg bias", accProjected: "projected", accActual: "actual",
    confidence: "Confidence", reasoning: "Reasoning", context: "Context used",
    conclusion: "Conclusion", conclusionUp: "Goes up", conclusionDown: "Goes down", conclusionFlat: "Stays the same",
    holidayCtx: "Holiday", mxnCtx: "MXN/USD", dieselCtx: "Diesel", newsCtx: "Recent news headlines used", weatherCtx: "Weather (Michoacán)",
    daysUntil: "days until", historicalMove: "historical move",
    vs90d: "vs 90-day avg",
    statusPending: "Pending review", statusApproved: "Approved", statusRejected: "Rejected",
    approve: "Approve", reject: "Reject", reviewedAt: "Reviewed",
    confHigh: "High", confMedium: "Medium", confLow: "Low",
    scopeNote: "v1 scope: Blackberry only. Both categories (Conventional/Organic) always show a card — the base price is a statistical Holt-Winters projection from historical market/sales data, never gated on or sourced from a real order, so you can compare it against what a client actually offers once an order arrives. Each card shows the ACTIVE next-week projection only, refreshed as new context arrives, never reset by that refresh once you've approved/rejected it. Organic's statistical model is fit on GFI's own historical price data since no market source tracks a live Organic feed. News headlines (NewsData.io, US+MX) and LIVE-ONLY weather (today's conditions + 5-day forecast for Michoacán, no historical anomaly detection yet) are included.",
    categoryConventional: "Conventional", categoryOrganic: "Organic",
    weeklyPriceTitle: "Firm order prices — previous, current + next week",
    weeklyPriceHint: "Cases-weighted averages straight from the real POs already captured in Weekly PO's — never typed in by hand. A product only shows up here if it actually has a firm order that week; nothing is estimated or invented. Previous week only appears if it has real data.",
    /* 2026-10-16, Arturo: "no se por que muestra fixed pricing y
       consigment pricing si eso ya no existe asi" — Fixed/Semi-fixed
       and Consignment are both captured from the single "Weekly PO's"
       screen now (App.js: fixed_pricing → "Weekly PO's", Consignment
       Pricing retired from the nav 2026-10-08), even though the data
       itself still lives in 2 separate tables (fixed_pos/
       consignment_pos) because the pricing MATH genuinely differs — see
       weeklyWeightedPrices.js's own header comment. Relabeled to match
       current terminology instead of naming 2 modules that no longer
       exist in the sidebar. */
    weeklyPriceFixedTitle: "Weekly PO's — Fixed/Semi-fixed Terms", weeklyPriceConsignmentTitle: "Weekly PO's — Consignment Terms",
    weeklyPriceWeekPrev: "Previous week", weeklyPriceWeekCurrent: "This week", weeklyPriceWeekNext: "Pending Orders",
    weeklyPriceEmpty: "No firm orders in this window yet.",
    colWeek: "Week", colProduct: "Product", colCategory: "Category", colCases: "Cases",
    colCustomer: "Customer", colGF: "GF", colGrower: "Grower",
  },
  es: {
    title: "Registro Analista IA", subtitle: "Ajustes de precio diarios razonados por Claude — revisa y aprueba/rechaza antes de que signifiquen algo operacionalmente.",
    running: "Ejecutando…",
    runCurrentWeek: "Correr semana actual", runNextWeek: "Correr próxima semana",
    runNoActiveCategories: "Corrió bien, pero no hubo suficiente historial para construir una proyección estadística de ninguna categoría — todavía no hay nada que mostrar.",
    empty: "Sin proyección activa todavía — da clic en Ejecutar ahora, o espera el refresco automático de mañana.",
    baseForecast: "Precio de esta semana (estimado del modelo)", adjustedPrice: "Proyección próxima semana", adjustment: "Ajuste",
    thisWeekFirmOrderLabel: "Orden real esta semana", thisWeekFirmOrderHint: "para comparar, no es la base del modelo",
    projectionForWeek: "proyección para la semana", lastRefreshed: "Última actualización",
    accTrackRecord: "Historial reciente", accAvgBias: "sesgo prom.", accProjected: "proyectado", accActual: "real",
    confidence: "Confianza", reasoning: "Razonamiento", context: "Contexto usado",
    conclusion: "Conclusión", conclusionUp: "Sube", conclusionDown: "Baja", conclusionFlat: "Se mantiene",
    holidayCtx: "Feriado", mxnCtx: "MXN/USD", dieselCtx: "Diésel", newsCtx: "Titulares de noticias recientes usados", weatherCtx: "Clima (Michoacán)",
    daysUntil: "días para", historicalMove: "movimiento histórico",
    vs90d: "vs promedio 90 días",
    statusPending: "Pendiente de revisión", statusApproved: "Aprobado", statusRejected: "Rechazado",
    approve: "Aprobar", reject: "Rechazar", reviewedAt: "Revisado",
    confHigh: "Alta", confMedium: "Media", confLow: "Baja",
    scopeNote: "Alcance v1: solo Blackberry. Ambas categorías (Convencional/Orgánico) siempre muestran tarjeta — el precio base es una proyección estadística Holt-Winters del historial de mercado/ventas, nunca condicionada a ni calculada de una orden real, para que puedas comparar contra lo que un cliente realmente ofrezca cuando llegue una orden. Cada tarjeta muestra ÚNICAMENTE la proyección activa de la próxima semana, se refresca con contexto más reciente, y nunca se reinicia a pendiente por ese refresco una vez que ya la aprobaste/rechazaste. El modelo estadístico de Orgánico se ajusta sobre el historial de precio propio de GFI porque ningún mercado reporta un feed en vivo de Orgánico. Incluye titulares de noticias (NewsData.io, EE.UU.+México) y clima EN VIVO (condiciones de hoy + pronóstico de 5 días para Michoacán, todavía sin detección de anomalías históricas).",
    categoryConventional: "Convencional", categoryOrganic: "Orgánico",
    weeklyPriceTitle: "Precios de órdenes firmes — semana anterior, actual y próxima",
    weeklyPriceHint: "Promedios ponderados por cajas, directo de los PO reales ya capturados en Weekly PO's — nunca capturado a mano. Un producto solo aparece aquí si de verdad tiene una orden firme esa semana; nada se estima ni se inventa. La semana anterior solo aparece si tiene datos reales.",
    weeklyPriceFixedTitle: "Weekly PO's — Términos Fijo/Semi-fijo", weeklyPriceConsignmentTitle: "Weekly PO's — Términos Consignación",
    weeklyPriceWeekPrev: "Semana anterior", weeklyPriceWeekCurrent: "Esta semana", weeklyPriceWeekNext: "Órdenes pendientes",
    weeklyPriceEmpty: "Sin órdenes firmes todavía en esta ventana.",
    colWeek: "Semana", colProduct: "Producto", colCategory: "Categoría", colCases: "Cajas",
    colCustomer: "Cliente", colGF: "GF", colGrower: "Productor",
  },
};

var CONFIDENCE_COLOR = { high: "#059669", medium: "#D97706", low: "#DC2626" };
var STATUS_STYLE = {
  pending: { bg: "rgba(217,119,6,0.10)", text: "#B45309", border: "rgba(217,119,6,0.3)" },
  approved: { bg: "rgba(5,150,105,0.10)", text: "#059669", border: "rgba(5,150,105,0.3)" },
  rejected: { bg: "rgba(220,38,38,0.10)", text: "#DC2626", border: "rgba(220,38,38,0.3)" },
};

function addDaysLocal(dateStr, delta) {
  var d = new Date(dateStr + "T00:00:00");
  d.setDate(d.getDate() + delta);
  return d.toISOString().slice(0, 10);
}
function fmtDateLong(dateStr, lang) {
  try { return new Date(dateStr + "T00:00:00").toLocaleDateString(lang === "es" ? "es-MX" : "en-US", { weekday: "long", year: "numeric", month: "long", day: "numeric" }); }
  catch (e) { return dateStr; }
}
function fmtDateTime(iso, lang) {
  try { return new Date(iso).toLocaleString(lang === "es" ? "es-MX" : "en-US", { month: "short", day: "2-digit", hour: "2-digit", minute: "2-digit" }); }
  catch (e) { return iso; }
}

/* Replaces the 4 manual price-quote cards (removed 2026-09-17, see
   weeklyWeightedPrices.js header) — a single board showing cases-
   weighted Customer/GF/Grower (Fixed) and Customer/Commission/
   Differential (Consignment) prices for the current + next ISO week,
   computed live from real fixed_pos/consignment_pos rows. Nothing is
   captured here — this is a read-only summary of firm orders already
   entered elsewhere in the app. */
function weekLabel(weekNum, weeks, t) {
  if (weekNum === weeks[0]) return t.weeklyPriceWeekPrev + " (W" + weekNum + ")";
  if (weekNum === weeks[1]) return t.weeklyPriceWeekCurrent + " (W" + weekNum + ")";
  if (weekNum === weeks[2]) return t.weeklyPriceWeekNext + " (W" + weekNum + ")";
  return "W" + weekNum;
}

function fmtMoney(n) { return n == null ? "—" : "$" + n.toFixed(2); }

function WeeklyWeightedPriceTable({ title, rows, weeks, t, columns }) {
  if (!rows.length) {
    return (
      <div style={{ padding: 14, borderRadius: 8, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", marginBottom: 10 }}>
        <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 6 }}>{title}</div>
        <div style={{ fontSize: 11, color: "var(--color-text-muted)", fontStyle: "italic" }}>{t.weeklyPriceEmpty}</div>
      </div>
    );
  }
  return (
    <div style={{ padding: 14, borderRadius: 8, background: "var(--color-bg-card)", border: "1px solid var(--color-border)", marginBottom: 10, overflowX: "auto" }}>
      <div style={{ fontSize: 12.5, fontWeight: 700, marginBottom: 8 }}>{title}</div>
      <table style={{ borderCollapse: "collapse", width: "100%", fontSize: 11.5 }}>
        <thead>
          <tr>
            {columns.map(function (c) { return (
              <th key={c.key} style={{ textAlign: c.numeric ? "right" : "left", padding: "4px 8px", borderBottom: "1px solid var(--color-border)", color: "var(--color-text-muted)", fontWeight: 700, fontSize: 10, textTransform: "uppercase", letterSpacing: "0.03em" }}>{c.label}</th>
            ); })}
          </tr>
        </thead>
        <tbody>
          {rows.map(function (r, i) { return (
            <tr key={i}>
              {columns.map(function (c) { return (
                <td key={c.key} style={{ textAlign: c.numeric ? "right" : "left", padding: "4px 8px", borderBottom: "1px solid var(--color-border)", fontFamily: c.numeric ? "'JetBrains Mono', monospace" : undefined }}>
                  {c.render ? c.render(r) : r[c.key]}
                </td>
              ); })}
            </tr>
          ); })}
        </tbody>
      </table>
    </div>
  );
}

function WeeklyWeightedPriceBoard({ t, lang, fixedPos, consignmentPos }) {
  var weeks = getPrevCurrentNextWeeks();
  var fixedRows = fillWeekGaps(buildWeightedFixedPrices(fixedPos, weeks), weeks, ["gfPrice", "growerPrice"]);
  var consignRows = fillWeekGaps(buildWeightedConsignmentPrices(consignmentPos, weeks), weeks, ["gfPrice", "growerPrice"]);

  /* fixed_pos/consignment_pos store the SHORT code ("OG"/"CV"), not
     the full word — confirmed against real production data
     2026-09-17. Accept both so this keeps working if a row ever has
     the full word too (e.g. an older/manually-typed record). */
  var categoryLabel = function (c) {
    return c === "Organic" || c === "OG" ? t.categoryOrganic
      : c === "Conventional" || c === "CV" ? t.categoryConventional
      : (c || "—");
  };

  var fixedColumns = [
    { key: "week", label: t.colWeek, render: function (r) { return weekLabel(r.week, weeks, t); } },
    { key: "productBase", label: t.colProduct },
    { key: "category", label: t.colCategory, render: function (r) { return categoryLabel(r.category); } },
    { key: "cases", label: t.colCases, numeric: true, render: function (r) { return r.cases.toLocaleString(lang === "es" ? "es-MX" : "en-US"); } },
    { key: "customerPrice", label: t.colCustomer, numeric: true, render: function (r) { return fmtMoney(r.customerPrice); } },
    { key: "gfPrice", label: t.colGF, numeric: true, render: function (r) { return fmtMoney(r.gfPrice); } },
    { key: "growerPrice", label: t.colGrower, numeric: true, render: function (r) { return fmtMoney(r.growerPrice); } },
  ];
  var consignColumns = [
    { key: "week", label: t.colWeek, render: function (r) { return weekLabel(r.week, weeks, t); } },
    { key: "productBase", label: t.colProduct },
    { key: "category", label: t.colCategory, render: function (r) { return categoryLabel(r.category); } },
    { key: "cases", label: t.colCases, numeric: true, render: function (r) { return r.cases.toLocaleString(lang === "es" ? "es-MX" : "en-US"); } },
    { key: "customerPrice", label: t.colCustomer, numeric: true, render: function (r) { return fmtMoney(r.customerPrice); } },
    { key: "gfPrice", label: t.colGF, numeric: true, render: function (r) { return fmtMoney(r.gfPrice); } },
    { key: "growerPrice", label: t.colGrower, numeric: true, render: function (r) { return fmtMoney(r.growerPrice); } },
  ];
  return (
    <div style={{ marginBottom: 6 }}>
      <div style={{ fontSize: 13.5, fontWeight: 700, marginBottom: 2 }}>{t.weeklyPriceTitle}</div>
      <div style={{ fontSize: 10.5, color: "var(--color-text-muted)", marginBottom: 10 }}>{t.weeklyPriceHint}</div>
      <WeeklyWeightedPriceTable title={t.weeklyPriceFixedTitle} rows={fixedRows} weeks={weeks} t={t} columns={fixedColumns} />
      {/* 2026-10-16, Arturo: "ya no tengo Weekly PO's — Consignment
          Terms" — no client is currently captured under Consignment
          terms, so this table was always empty ("No firm orders in this
          window yet.") and just took up space. Hidden (not deleted —
          same "never show empty state" precedent as the rest of this
          app, and the table comes back on its own the moment a real
          Consignment PO exists again, no code change needed) rather
          than removed outright like the truly-redundant Summary table
          above. */}
      {consignRows.length > 0 && <WeeklyWeightedPriceTable title={t.weeklyPriceConsignmentTitle} rows={consignRows} weeks={weeks} t={t} columns={consignColumns} />}
    </div>
  );
}

export default function AIAnalystLog({ lang: globalLang }) {
  var lang = globalLang || "es";
  var t = T[lang === "es" ? "es" : "en"];
  var [entries, setEntries] = useState([]);
  var [loading, setLoading] = useState(true);
  var [running, setRunning] = useState(false);
  var [runError, setRunError] = useState("");
  var [fixedPos, setFixedPos] = useState([]);
  var [consignmentPos, setConsignmentPos] = useState([]);

  var load = useCallback(function () {
    setLoading(true);
    loadFromSupabase("ai_analyst_log", { force: true }).then(function (rows) {
      var sorted = (rows || []).slice().sort(function (a, b) { return a.logDate < b.logDate ? 1 : -1; });
      /* Show only the ACTIVE projection per category — never a scrolling
         history of daily/weekly snapshots (Arturo, 2026-09-17: "ahora no
         muestres 3 semanas, solo muestra la proyeccion de la proxima
         semana"). Since the log is now keyed one row per (week, category)
         and continuously refreshed while that week is still "next week"
         (see runAiForecastAnalysis in ai-forecast-analysis.js), the most
         recent row per category IS the current active projection — older
         rows stay in the database as a real rolling archive of past
         projections (useful later for an accuracy comparison), just not
         shown here by default. */
      var latestByCategory = {};
      var order = [];
      sorted.forEach(function (e) {
        var cat = e.category || "";
        if (!(cat in latestByCategory)) { latestByCategory[cat] = e; order.push(cat); }
      });
      setEntries(order.map(function (cat) { return latestByCategory[cat]; }));
    }).catch(function () { setEntries([]); }).finally(function () { setLoading(false); });
  }, []);

  var loadWeeklyPriceSources = useCallback(function () {
    loadFromSupabase("fixed_pos", { force: true }).then(function (rows) { setFixedPos(rows || []); }).catch(function () { setFixedPos([]); });
    loadFromSupabase("consignment_pos", { force: true }).then(function (rows) { setConsignmentPos(rows || []); }).catch(function () { setConsignmentPos([]); });
  }, []);

  useEffect(function () { load(); loadWeeklyPriceSources(); }, [load, loadWeeklyPriceSources]);

  /* Which week to run against — 0 (current) or 1 (next). Arturo,
     2026-09-17: "supongo que como no he capturado las ordenes de la
     siguiente semana... me dice que no hay entries, pero necesito
     correr esta semana, agrega la opcion de solo correr semana actual
     y la siguiente, la anterior ya no puede ejecutarse" — he only had
     next week's POs captured so far, not this week's, so the
     always-"today"-gated run found nothing. Only these 2 choices are
     offered (never "previous" — a past week already happened, there's
     nothing left to project). */
  var handleRunNow = function (weekOffset) {
    setRunning(true);
    setRunError("");
    /* Surface EVERY failure mode, not just result.success===false — a
       silently-swallowed HTTP error (Netlify function timeout, 502,
       etc., which returns non-JSON) used to parse-fail into `{}`, whose
       `success` key is undefined (not `false`), so the old check never
       fired and "Run now" looked like it did nothing at all (Arturo,
       2026-09-17: "ejecuta pero no hace nada"). Now any non-2xx HTTP
       status, any JSON-parse failure, and any success:false all show a
       real error message instead of failing silently. */
    fetch("/.netlify/functions/ai-forecast-analysis", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ weekOffset: weekOffset === 1 ? 1 : 0 }),
    })
      .then(function (res) {
        return res.text().then(function (text) {
          var parsed = null;
          try { parsed = text ? JSON.parse(text) : null; } catch (e) { parsed = null; }
          if (!res.ok) {
            throw new Error("HTTP " + res.status + (parsed && parsed.error ? ": " + parsed.error : text ? ": " + text.slice(0, 200) : ""));
          }
          if (!parsed) throw new Error("Empty or invalid response (HTTP " + res.status + ")");
          return parsed;
        });
      })
      .then(function (result) {
        if (result && result.success === false) setRunError(result.error || "Error");
        else if (result && result.entries && result.entries.length === 0) setRunError(t.runNoActiveCategories);
        load();
      })
      .catch(function (err) { setRunError(err && err.message ? err.message : String(err)); })
      .finally(function () { setRunning(false); });
  };

  var handleReview = function (entry, status) {
    saveToSupabase("ai_analyst_log", Object.assign({}, entry, { status: status, reviewedAt: new Date().toISOString() }))
      .then(function () { load(); })
      .catch(function () {});
  };

  /* 2026-10-16, Arturo: "de que me sirve el AI Analyst Log si trabaja
     con los precios que me dan clientes en las ordenes?... necesito que
     sea un predictor... sin que necesite los precios que ya me dan" —
     this used to hide a category's card unless it had a real firm
     order THIS week (fixed_pos, current ISO week, real cases), mirroring
     the Netlify function's own old generation gate. Both gates are gone
     now: ai-forecast-analysis.js always projects both categories from
     its statistical model (see that file's 2026-10-16 header comment),
     so this screen always shows both cards too — exactly so Arturo can
     compare the model's projection against a real order once one
     arrives, instead of only ever seeing a card after the fact. */
  var visibleEntries = entries;

  /* Customer/GF/Grower breakdown for each card (Arturo, 2026-09-17:
     "necesito que agrege 2 lineas... el ajuste de -3.04 se aplica a
     los 3 precios que manejo") — the AI only ever reasons about ONE
     price (Customer, via gatherWeightedCustomerPrice), but Arturo's
     real business has 3 linked prices per PO line (Customer/GF/
     Grower, same triple as the weekly price board above). Rather than
     have Claude reason 3x independently (it only ever sees the
     Customer number, and GF/Grower move in lockstep with it by
     construction — see FixedPricingRegistry.js/consignmentDerivedPrices),
     this week's REAL GF/Grower prices are looked up from the same
     fixed_pos data already loaded for the board, and the adjustment_pct
     Claude computed for Customer is applied uniformly to project GF and
     Grower too. */
  var thisWeek = getPrevCurrentNextWeeks()[1];
  var thisWeekFixedByCategory = {};
  /* 2026-10-16, Arturo: "el AI Analyst Log esta mezclando blueberries y
     blackberries en su pronostico" — buildWeightedFixedPrices returns
     one row PER PRODUCT (Blackberry, Blueberry, ...) for "this week" +
     category, but this map was keying by category ALONE, so whichever
     product's row came last in the (alphabetically-sorted) array
     silently overwrote the other — "Blueberry" sorts after "Blackberry",
     so an Organic card ended up showing Blueberry's $57/$56/$56 instead
     of Blackberry's real ~$21 under a "Blackberry — Organic" title. The
     AI's own analysis is (and only ever has been) Blackberry-only — see
     "v1 scope: Blackberry only" above — so this lookup must filter to
     the same product before keying by category. */
  buildWeightedFixedPrices(fixedPos, [thisWeek]).forEach(function (r) {
    if ((r.productBase || r.productName) !== "Blackberry") return;
    var full = r.category === "OG" ? "Organic" : r.category === "CV" ? "Conventional" : r.category;
    thisWeekFixedByCategory[full] = r;
  });

  return (
    <div style={{ maxHeight: "calc(100vh - 120px)", overflowY: "auto", paddingRight: 4 }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 14, gap: 10, flexWrap: "wrap" }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 700, margin: "0 0 4px" }}>{t.title}</h2>
          <p style={{ fontSize: 11, color: "var(--color-text-muted)", margin: 0 }}>{t.subtitle}</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <button
            type="button"
            onClick={function () { handleRunNow(0); }}
            disabled={running}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "rgba(42,122,111,0.08)", color: "var(--color-accent)", cursor: running ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
          >
            {running ? t.running : t.runCurrentWeek}
          </button>
          <button
            type="button"
            onClick={function () { handleRunNow(1); }}
            disabled={running}
            style={{ padding: "8px 16px", borderRadius: 6, border: "1px solid var(--color-accent)", background: "rgba(42,122,111,0.08)", color: "var(--color-accent)", cursor: running ? "not-allowed" : "pointer", fontSize: 12, fontWeight: 700, whiteSpace: "nowrap" }}
          >
            {running ? t.running : t.runNextWeek}
          </button>
        </div>
      </div>

      {runError && (
        <div style={{ padding: "10px 14px", borderRadius: 8, background: "rgba(220,38,38,0.08)", border: "1px solid rgba(220,38,38,0.25)", color: "#DC2626", fontSize: 11.5, marginBottom: 14 }}>{runError}</div>
      )}

      <WeeklyWeightedPriceBoard t={t} lang={lang} fixedPos={fixedPos} consignmentPos={consignmentPos} />

      <div style={{ padding: "8px 14px", borderRadius: 8, background: "rgba(107,114,128,0.08)", border: "1px solid var(--color-border)", fontSize: 10.5, color: "var(--color-text-muted)", marginBottom: 16 }}>
        {t.scopeNote}
      </div>

      {loading ? null : visibleEntries.length === 0 ? (
        <div style={{ padding: "24px 16px", textAlign: "center", color: "var(--color-text-muted)", fontStyle: "italic", fontSize: 12.5 }}>{t.empty}</div>
      ) : (
        visibleEntries.map(function (entry) {
          var ctx = entry.context || {};
          var statusKey = entry.status || "pending";
          var ss = STATUS_STYLE[statusKey] || STATUS_STYLE.pending;
          var confKey = String(entry.confidence || "").toLowerCase();
          var confColor = CONFIDENCE_COLOR[confKey] || "var(--color-text-secondary)";
          var confLabel = confKey === "high" ? t.confHigh : confKey === "medium" ? t.confMedium : confKey === "low" ? t.confLow : (entry.confidence || "—");
          var up = entry.adjustmentPct >= 0;
          var CONCLUSION_FLAT_THRESHOLD = 1; // |adjustment_pct| below this reads as "stays the same," not a real move
          var conclusionKey = entry.adjustmentPct == null ? null
            : entry.adjustmentPct > CONCLUSION_FLAT_THRESHOLD ? "up"
            : entry.adjustmentPct < -CONCLUSION_FLAT_THRESHOLD ? "down"
            : "flat";
          /* Computed fresh from logDate every render — never parsed out
             of legacy_id. A row written before the week-based legacy_id
             scheme existed still has an old date-shaped id ("2026-09-
             17|Organic"), and parsing that would keep showing a raw
             date forever until that exact row gets regenerated (Arturo,
             2026-09-17, twice: "no dice la semana, dice fecha, debe
             decir la week"). logDate is always a real date regardless
             of legacy_id format, so deriving "the week after logDate"
             here is correct for every row, old or new, with zero
             dependency on how it was written. */
          var projectedWeekTag = entry.logDate ? "W" + getISOWeek(addDaysLocal(entry.logDate, 7)) : null;
          /* 2026-10-16, Arturo: the Customer row's "this week" value must
             ALWAYS be the statistical model's own estimate
             (entry.baseForecast) — never silently swapped for a real
             order's price when one happens to exist, which is exactly
             the Blackberry/Blueberry-mixing bug class he just hit (a
             card's own number contradicting its own label). The model
             doesn't produce independent GF/Grower forecasts (only
             Customer), so those 2 rows still show the real order's
             actual GF/Grower when one exists this week (clearly a real
             number, not a model one) and "—" otherwise — next week's
             GF/Grower projection is Claude's Customer adjustment_pct
             applied uniformly to whichever GF/Grower base is showing. A
             real Customer order this week, when one exists, is shown
             separately below the table (thisWeekFirmOrder) so it's
             always comparable against the model's own row without ever
             overwriting it. */
          var fixedRow = thisWeekFixedByCategory[entry.category];
          var pctMult = entry.adjustmentPct != null ? 1 + entry.adjustmentPct / 100 : null;
          var priceRows = [
            { label: t.colCustomer, thisWeek: entry.baseForecast, nextWeek: entry.adjustedPrice },
            { label: t.colGF, thisWeek: fixedRow ? fixedRow.gfPrice : null, nextWeek: fixedRow && fixedRow.gfPrice != null && pctMult != null ? fixedRow.gfPrice * pctMult : null },
            { label: t.colGrower, thisWeek: fixedRow ? fixedRow.growerPrice : null, nextWeek: fixedRow && fixedRow.growerPrice != null && pctMult != null ? fixedRow.growerPrice * pctMult : null },
          ];
          return (
            <div key={entry.id || entry.logDate} style={{ background: "var(--color-bg-card)", border: "1px solid var(--color-border)", borderRadius: 10, padding: 16, marginBottom: 12 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10, flexWrap: "wrap", gap: 8 }}>
                <div>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>{entry.product}{entry.category ? " — " + entry.category : ""}{projectedWeekTag ? " — " + t.projectionForWeek + " " + projectedWeekTag : ""}</div>
                  <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.lastRefreshed}: {fmtDateLong(entry.logDate, lang)}</div>
                </div>
                <span style={{ padding: "3px 10px", borderRadius: 10, fontSize: 10.5, fontWeight: 700, background: ss.bg, color: ss.text, border: "1px solid " + ss.border }}>
                  {statusKey === "approved" ? t.statusApproved : statusKey === "rejected" ? t.statusRejected : t.statusPending}
                </span>
              </div>

              <table style={{ borderCollapse: "collapse", width: "100%", marginBottom: 12, fontSize: 12.5 }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: "left", padding: "3px 8px 3px 0", fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}></th>
                    <th style={{ textAlign: "right", padding: "3px 8px", fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.baseForecast}</th>
                    <th style={{ textAlign: "right", padding: "3px 0 3px 8px", fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.adjustedPrice}</th>
                  </tr>
                </thead>
                <tbody>
                  {priceRows.map(function (pr) { return (
                    <tr key={pr.label}>
                      <td style={{ padding: "3px 8px 3px 0", fontWeight: 700, borderTop: "1px solid var(--color-border)" }}>{pr.label}</td>
                      <td style={{ padding: "3px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", borderTop: "1px solid var(--color-border)" }}>{pr.thisWeek != null ? "$" + pr.thisWeek.toFixed(2) : "—"}</td>
                      <td style={{ padding: "3px 0 3px 8px", textAlign: "right", fontFamily: "'JetBrains Mono', monospace", color: "var(--color-accent)", fontWeight: 700, borderTop: "1px solid var(--color-border)" }}>{pr.nextWeek != null ? "$" + pr.nextWeek.toFixed(2) : "—"}</td>
                    </tr>
                  ); })}
                </tbody>
              </table>

              {ctx.thisWeekFirmOrder && (
                <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 12, padding: "6px 10px", borderRadius: 6, background: "rgba(42,122,111,0.06)" }}>
                  {t.thisWeekFirmOrderLabel}: <b style={{ fontFamily: "'JetBrains Mono', monospace" }}>${ctx.thisWeekFirmOrder.price.toFixed(2)}</b> ({fmtDateLong(ctx.thisWeekFirmOrder.asOfDate, lang)}) — {t.thisWeekFirmOrderHint}
                </div>
              )}

              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(140px, 1fr))", gap: 10, marginBottom: 12 }}>
                <div>
                  <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.adjustment}</div>
                  <div style={{ fontSize: 16, fontWeight: 700, fontFamily: "'JetBrains Mono', monospace", color: up ? "#059669" : "#DC2626" }}>{up ? "▲ +" : "▼ "}{entry.adjustmentPct}%</div>
                </div>
                <div>
                  <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.confidence}</div>
                  <div style={{ fontSize: 13, fontWeight: 700, color: confColor }}>{confLabel}</div>
                </div>
              </div>

              {entry.reasoning && (
                <div style={{ marginBottom: 10 }}>
                  <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{t.reasoning}</div>
                  <div style={{ fontSize: 12, color: "var(--color-text-secondary)", lineHeight: 1.5 }}>{entry.reasoning}</div>
                </div>
              )}

              {conclusionKey && (
                <div style={{ display: "flex", alignItems: "flex-start", gap: 10, marginBottom: 12, flexWrap: "wrap" }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                    <span style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em" }}>{t.conclusion}:</span>
                    <span style={{
                      padding: "3px 12px", borderRadius: 10, fontSize: 12, fontWeight: 700, whiteSpace: "nowrap",
                      background: conclusionKey === "up" ? "rgba(5,150,105,0.10)" : conclusionKey === "down" ? "rgba(220,38,38,0.10)" : "rgba(107,114,128,0.10)",
                      color: conclusionKey === "up" ? "#059669" : conclusionKey === "down" ? "#DC2626" : "var(--color-text-secondary)",
                    }}>
                      {conclusionKey === "up" ? "▲ " + t.conclusionUp : conclusionKey === "down" ? "▼ " + t.conclusionDown : "→ " + t.conclusionFlat}
                    </span>
                  </div>
                  {entry.plainExplanation && (
                    <div style={{ fontSize: 12, color: "var(--color-text-secondary)", fontStyle: "italic", lineHeight: 1.5, flex: "1 1 240px" }}>
                      {entry.plainExplanation}
                    </div>
                  )}
                </div>
              )}

              {ctx.stale && (
                <div style={{ padding: "6px 10px", borderRadius: 6, background: "rgba(217,119,6,0.10)", border: "1px solid rgba(217,119,6,0.3)", color: "#B45309", fontSize: 10.5, marginBottom: 10 }}>
                  {lang === "es"
                    ? "⚠ Base de precio histórica, no en vivo (último dato real: " + ctx.lastRealDate + ", hace " + ctx.daysStale + " días) — " + ctx.priceSourceNote
                    : "⚠ Historical, not live price base (last real data point: " + ctx.lastRealDate + ", " + ctx.daysStale + " days ago) — " + ctx.priceSourceNote}
                </div>
              )}

              <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 4 }}>{t.context}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginBottom: 12 }}>
                {ctx.marketTrend && ctx.marketTrend.lastPrice != null && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {lang === "es" ? "USDA (referencia): " : "USDA (reference): "}${ctx.marketTrend.lastPrice} ({ctx.marketTrend.lastDate})
                  </span>
                )}
                {ctx.salesTrend && ctx.salesTrend.lastPrice != null && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {lang === "es" ? "Ventas reales Nexus: " : "Real Nexus sales: "}${ctx.salesTrend.lastPrice} ({ctx.salesTrend.lastDate})
                  </span>
                )}
                {ctx.mexicoExport && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {lang === "es" ? "México exportación (otros): " : "Mexico export (others): "}${ctx.mexicoExport.price} ({ctx.mexicoExport.quotedDate})
                  </span>
                )}
                {ctx.nextWeekFirmOrder && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "rgba(42,122,111,0.08)", border: "1px solid var(--color-accent)", color: "var(--color-accent)", fontWeight: 700 }}>
                    {lang === "es" ? "Órdenes ya firmes próxima semana: " : "Next week's firm orders already placed: "}${ctx.nextWeekFirmOrder.price} ({ctx.nextWeekFirmOrder.weekTag})
                  </span>
                )}
                {ctx.recentAccuracy && (
                  <span
                    title={ctx.recentAccuracy.recent.map(function (s) { return s.weekTag + ": " + t.accProjected + " $" + s.projected + " / " + t.accActual + " $" + s.actual + " (" + (s.errorPct > 0 ? "+" : "") + s.errorPct + "%)"; }).join(" | ")}
                    style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: Math.abs(ctx.recentAccuracy.avgSignedErrorPct) > 10 ? "rgba(220,38,38,0.08)" : "rgba(107,114,128,0.08)", border: "1px solid " + (Math.abs(ctx.recentAccuracy.avgSignedErrorPct) > 10 ? "rgba(220,38,38,0.3)" : "var(--color-border)"), color: Math.abs(ctx.recentAccuracy.avgSignedErrorPct) > 10 ? "#DC2626" : "var(--color-text-secondary)" }}
                  >
                    {t.accTrackRecord} ({ctx.recentAccuracy.recent.length}): {t.accAvgBias} {ctx.recentAccuracy.avgSignedErrorPct > 0 ? "+" : ""}{ctx.recentAccuracy.avgSignedErrorPct}%
                  </span>
                )}
                {ctx.holiday && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {t.holidayCtx}: {ctx.holiday.name} ({ctx.holiday.daysUntil} {t.daysUntil}{ctx.holiday.historicalSpikePct != null ? ", " + t.historicalMove + " " + (ctx.holiday.historicalSpikePct > 0 ? "+" : "") + ctx.holiday.historicalSpikePct + "%" : ""})
                  </span>
                )}
                {ctx.mxnUsd && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {t.mxnCtx}: {ctx.mxnUsd.latest} ({t.vs90d} {ctx.mxnUsd.avg90d}, Δ{ctx.mxnUsd.deltaFromAvg > 0 ? "+" : ""}{ctx.mxnUsd.deltaFromAvg})
                  </span>
                )}
                {ctx.diesel && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: "var(--color-bg)", border: "1px solid var(--color-border)", color: "var(--color-text-secondary)" }}>
                    {t.dieselCtx}: ${ctx.diesel.latest} ({t.vs90d} ${ctx.diesel.avg90d}, Δ{ctx.diesel.deltaFromAvg > 0 ? "+" : ""}{ctx.diesel.deltaFromAvg})
                  </span>
                )}
                {ctx.weather && (
                  <span style={{ padding: "3px 8px", borderRadius: 8, fontSize: 10, background: ctx.weather.alerts && ctx.weather.alerts.length > 0 ? "rgba(220,38,38,0.08)" : "var(--color-bg)", border: "1px solid " + (ctx.weather.alerts && ctx.weather.alerts.length > 0 ? "rgba(220,38,38,0.3)" : "var(--color-border)"), color: ctx.weather.alerts && ctx.weather.alerts.length > 0 ? "#DC2626" : "var(--color-text-secondary)", fontWeight: ctx.weather.alerts && ctx.weather.alerts.length > 0 ? 700 : 400 }}
                    title={ctx.weather.alerts && ctx.weather.alerts.length > 0 ? ctx.weather.alerts.join("; ") : ""}>
                    {t.weatherCtx}: {ctx.weather.currentTempC != null ? ctx.weather.currentTempC + "°C" : "—"}{ctx.weather.alerts && ctx.weather.alerts.length > 0 ? " ⚠ " + ctx.weather.alerts.length + (lang === "es" ? " alerta(s)" : " alert(s)") : ""}
                  </span>
                )}
              </div>

              {ctx.news && ctx.news.length > 0 && (
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 9.5, color: "var(--color-text-muted)", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", marginBottom: 3 }}>{t.newsCtx}</div>
                  <ul style={{ margin: 0, paddingLeft: 16, fontSize: 10.5, color: "var(--color-text-secondary)", lineHeight: 1.6 }}>
                    {ctx.news.map(function (item, i) { return <li key={i}>{item.title} <span style={{ color: "var(--color-text-muted)" }}>({item.source})</span></li>; })}
                  </ul>
                </div>
              )}

              {statusKey === "pending" ? (
                <div style={{ display: "flex", gap: 8 }}>
                  <button type="button" onClick={function () { handleReview(entry, "approved"); }} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid #059669", background: "rgba(5,150,105,0.08)", color: "#059669", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.approve}</button>
                  <button type="button" onClick={function () { handleReview(entry, "rejected"); }} style={{ padding: "7px 16px", borderRadius: 6, border: "1px solid #DC2626", background: "rgba(220,38,38,0.08)", color: "#DC2626", cursor: "pointer", fontSize: 12, fontWeight: 700 }}>{t.reject}</button>
                </div>
              ) : entry.reviewedAt ? (
                <div style={{ fontSize: 10, color: "var(--color-text-muted)" }}>{t.reviewedAt}: {fmtDateTime(entry.reviewedAt, lang)}</div>
              ) : null}
            </div>
          );
        })
      )}
    </div>
  );
}
